import { useState } from "react";
import {
	Box,
	Typography,
	TextField,
	Button,
	IconButton,
	Alert,
} from "@mui/material";
import { AttachFile, DeleteOutline } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { validateTextField } from "../../utils/validation";
import { useSupportMutation } from "../../services/queryHooks";
import { Loading } from "../../components/common/Loading";

export default function Help() {
	const theme = useTheme();
	const { t } = useTranslation();

	const [form, setForm] = useState({
		topic: "",
		description: "",
	});
	const [attachments, setAttachments] = useState([]);
	const [errors, setErrors] = useState({
		topicError: "",
		descriptionError: "",
	});
	const [successMessage, setSuccessMessage] = useState("");

	const { mutate, isPending } = useSupportMutation();

	const handleSubmit = (e) => {
		e.preventDefault();
		if (!validateInputs()) return;

		mutate(
			{
				topic: form.topic,
				description: form.description,
				attachments,
			},
			{
				onSuccess: () => {
					setForm({ topic: "", description: "" });
					setAttachments([]);
					setSuccessMessage(t("supportRequestSent"));
				},
			}
		);
	};

	const resetErrors = () => {
		setErrors({
			topicError: "",
			descriptionError: "",
		});
	};

	const validateInputs = () => {
		resetErrors();
		let allErrors = {};

		const { topic, description } = form;

		const topicError = validateTextField(topic, t, {
			fieldName: t("topic"),
			maxLength: 50,
		});

		if (topicError) {
			allErrors.topicError = topicError;
		}
		const descriptionError = validateTextField(description, t, {
			fieldName: t("description"),
			maxLength: 500,
		});

		if (descriptionError) {
			allErrors.descriptionError = descriptionError;
		}

		if (Object.keys(allErrors).length > 0) {
			setErrors(allErrors);
			return false;
		}

		return true;
	};

	const MAX_FILES = 5;
	const MAX_FILE_SIZE = 5 * 1024 * 1024;

	const handleAddAttachments = (files) => {
		const newFiles = Array.from(files);

		setAttachments((prev) => {
			const combined = [...prev];

			for (const file of newFiles) {
				if (combined.length >= MAX_FILES) break;
				if (file.size > MAX_FILE_SIZE) continue;

				combined.push(file);
			}

			return combined;
		});
	};

	const boxStyles = {
		display: "flex",
		flexDirection: "column",
		alignSelf: "start",
		backgroundColor: theme.palette.background.paper,
		borderRadius: "10px",
		m: 2,
		p: 2,
	};

	return (
		<>
			{isPending && <Loading />}
			{successMessage && (
				<Alert
					severity="success"
					variant="outlined"
					onClose={() => setSuccessMessage("")}
					sx={{ m: 2, position: "relative" }}
				>
					{successMessage}
				</Alert>
			)}
			<Box sx={boxStyles}>
				<Typography variant="h6">
					{t("contactApplicationSupport")}
				</Typography>
				<form noValidate onSubmit={handleSubmit}>
					<TextField
						sx={{ mt: 2 }}
						autoFocus
						fullWidth
						type="text"
						label={t("topic")}
						value={form.topic}
						onChange={(e) =>
							setForm({ ...form, topic: e.target.value })
						}
						error={errors.topicError ? true : false}
						helperText={errors.topicError}
					/>
					<TextField
						sx={{ mt: 2 }}
						label={t("pleaseDescribeTheIssue")}
						multiline
						fullWidth
						rows={4}
						value={form.description}
						onChange={(e) =>
							setForm({ ...form, description: e.target.value })
						}
						error={errors.descriptionError ? true : false}
						helperText={errors.descriptionError}
					/>
					<input
						accept=".png,.jpg,.jpeg,.pdf"
						style={{ display: "none" }}
						id="support-attachments"
						type="file"
						multiple
						onChange={(e) => handleAddAttachments(e.target.files)}
					/>
					<label htmlFor="support-attachments">
						<Button
							component="span"
							variant="outlined"
							startIcon={<AttachFile />}
							sx={{ mt: 2 }}
						>
							{t("addAttachments")}
						</Button>
					</label>
					{attachments.length > 0 && (
						<Box sx={{ mt: 1 }}>
							{attachments.map((file, index) => (
								<Box
									key={index}
									sx={{
										display: "flex",
										alignItems: "center",
										justifyContent: "space-between",
										mt: 0.5,
									}}
								>
									<Typography variant="body1">
										{file.name} (
										{Math.round(file.size / 1024)} KB)
									</Typography>
									<IconButton
										aria-label="delete"
										onClick={() =>
											setAttachments((prev) =>
												prev.filter(
													(_, i) => i !== index
												)
											)
										}
									>
										<DeleteOutline fontSize="small" />
									</IconButton>
								</Box>
							))}
						</Box>
					)}

					<Box
						sx={{
							display: "flex",
							justifyContent: "flex-end",
							mt: 2,
						}}
					>
						<Button type="submit" variant="contained">
							{isPending ? t("sending") : t("reportIssue")}
						</Button>
					</Box>
				</form>
			</Box>
		</>
	);
}
